import type { FormEvent } from 'react';
import styles from './JobApplicationDemoPage.module.css';

/**
 * `/jobApplication` — mirrors Flutter's prototype WebJobApplication
 * (presentation/web_job_application/ + application/web_job_application/): a bare page, no header
 * or footer, rendering the four hard-coded fields of JobApplicationState.initial().formData.
 *
 * Kept as inert as Flutter's:
 *   - the checkbox and radio buttons read their value from a map nothing writes to, so they
 *     never change;
 *   - Submit validates through a GlobalKey that isn't attached to the Form, so it does nothing
 *     (no validation messages, no request).
 * Labels are shown as-is, like Flutter (no translation).
 */

interface DemoField {
  type: 'dropdown' | 'text' | 'checkbox' | 'radio';
  label: string;
  options?: string[];
  required?: boolean;
}

const FORM_DATA: DemoField[] = [
  { type: 'dropdown', label: 'Select a Color', options: ['Red', 'Green', 'Blue'], required: true },
  { type: 'text', label: 'Enter your Name', required: true },
  { type: 'checkbox', label: 'Accept Terms and Conditions' },
  { type: 'radio', label: 'Choose an Option', options: ['Option 1', 'Option 2', 'Option 3'], required: true },
];

const noop = () => {};

export function JobApplicationDemoPage() {
  const handleSubmit = (e: FormEvent) => {
    // Flutter: _formKey.currentState?.validate() is null here, so nothing happens
    e.preventDefault();
  };

  return (
    <main className={styles.page}>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {FORM_DATA.map((field) => (
          <DemoFormField key={field.label} field={field} />
        ))}
        <button type="submit" className={styles.submit}>Submit</button>
      </form>
    </main>
  );
}

function DemoFormField({ field }: { field: DemoField }) {
  const id = `job-application-${field.label.replace(/\W+/g, '-').toLowerCase()}`;
  switch (field.type) {
    case 'dropdown':
      return (
        <div className={`${styles.field} ${styles.dropdown}`}>
          <label htmlFor={id} className={styles.label}>{field.label}</label>
          <select id={id} className={styles.input} defaultValue="">
            <option value="" disabled hidden />
            {field.options?.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
      );
    case 'text':
      return (
        <div className={styles.field}>
          <label htmlFor={id} className={styles.label}>{field.label}</label>
          <input id={id} type="text" className={styles.input} />
        </div>
      );
    case 'checkbox':
      return (
        <label className={styles.choice}>
          <input type="checkbox" checked={false} onChange={noop} />
          {field.label}
        </label>
      );
    case 'radio':
      return (
        <fieldset className={styles.group}>
          <legend className={styles.legend}>{field.label}</legend>
          {field.options?.map((option) => (
            <label key={option} className={styles.choice}>
              <input type="radio" name={id} value={option} checked={false} onChange={noop} />
              {option}
            </label>
          ))}
        </fieldset>
      );
    default:
      return null;
  }
}
